import { useEffect, useState } from 'react';
import { voice } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button, SimpleGrid, Badge, Title } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader, DataTable, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { NumberInput, Switch, Textarea, TextInput, Select, SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { TenantName } from '../../components/TenantName';
import { formatDateTime, formatMinor } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import {
  useVoiceSettings, useVoiceTenants, useVoiceNodes, useVoiceAudit,
  useUpdateVoiceSettings, useKillVoice, useUpdateVoiceTenant, useVoiceCalls, useEndVoiceCall, useVoiceBans, useLiftVoiceBan, useVoiceReadiness
} from '../../data/useVoice';
import { useT } from '../../i18n/I18nProvider';
import { Supply } from './Supply';
import './voice.css';

type Settings = voice.PlatformVoiceSettings;
type SwitchKey = keyof Settings['switches'];
const MUTED = 'var(--rr-color-text-muted)';

const SWITCHES: SwitchKey[] = ['inbound', 'ai', 'outbound', 'international', 'recording', 'applications', 'self_activation', 'whatsapp'];
/** Limits counted in things, and limits counted in money (shown in rand, stored in cents). */
const COUNT_LIMITS = ['max_call_minutes', 'min_balance_minutes', 'billing_increment_seconds', 'concurrent_calls_per_tenant', 'concurrent_calls_platform', 'max_extensions_per_tenant', 'max_numbers_per_tenant'] as const;
const FRAUD_LIMITS = ['max_outbound_per_10_min', 'max_destinations_per_hour', 'max_short_calls_per_10_min', 'short_call_seconds'] as const;
const NUMBER_POLICY = ['rental_grace_days', 'release_after_days', 'application_hold_days'] as const;
const MONEY_LIMITS = ['tenant_hourly_cap_minor', 'tenant_daily_cap_minor', 'platform_hourly_cap_minor', 'platform_daily_cap_minor'] as const;

const words = (list: string[]) => list.join(', ');
const split = (text: string) => text.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);

/**
 * Reach Voice, the platform's side (2 October 2026).
 *
 * The owner asked for voice to be built switched off, behind a kill switch,
 * and to be "heavily settings based". So this page is the switch and the
 * settings, in that order: whether voice is on at all, which parts of it are
 * on, the ceilings no organisation can exceed, and who has asked for it.
 * Switching on asks for a reason and is refused until the terms are published;
 * the kill switch is one button, always visible while voice is on, and ends
 * every call in progress.
 */
export default function VoicePage() {
  const t = useT();
  const settings = useVoiceSettings();
  const tenants = useVoiceTenants();
  const nodes = useVoiceNodes();
  const audit = useVoiceAudit();
  const save = useUpdateVoiceSettings();
  const kill = useKillVoice();
  const updateTenant = useUpdateVoiceTenant();

  const current = settings.data?.data;
  const [draft, setDraft] = useState<Settings | null>(null);
  const [countries, setCountries] = useState('');
  const [prefixes, setPrefixes] = useState('');
  const [documents, setDocuments] = useState('');
  // WhatsApp calls (3 October 2026): the addresses Meta's calls come from.
  const [waSources, setWaSources] = useState('');
  const [reasonFor, setReasonFor] = useState<null | { kind: 'on' | 'off' | 'kill' } | { kind: 'suspend'; organisationId: string }>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!current) return;
    setDraft(current.settings);
    setCountries(words(current.settings.destinations.allowed_countries));
    setPrefixes(words(current.settings.destinations.blocked_prefixes));
    setDocuments((current.settings.numbers.required_documents ?? []).join('\n'));
    setWaSources((current.settings.whatsapp?.sources ?? []).join('\n'));
  }, [current]);

  const on = current?.master_enabled === true;
  const set = <K extends keyof Settings>(section: K, changes: Partial<Settings[K]> | Settings[K]) =>
    setDraft((d) => (d ? { ...d, [section]: typeof changes === 'object' && changes !== null && !Array.isArray(changes) ? { ...(d[section] as object), ...changes } : changes } as Settings : d));

  async function liftBlock(organisationId: string) {
    try {
      await updateTenant.mutateAsync({ organisationId, outboundBlocked: false });
      notifySuccess(t('voice.tenants.blockLifted'));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('voice.toast.failed'));
    }
  }

  async function saveSettings() {
    if (!draft) return;
    try {
      await save.mutateAsync({ settings: {
        ...draft,
        destinations: { allowed_countries: split(countries).map((c) => c.toUpperCase()), blocked_prefixes: split(prefixes) },
        // One document per line: what an application for a number must carry (RICA).
        numbers: { ...draft.numbers, required_documents: documents.split('\n').map((d) => d.trim()).filter(Boolean) },
        whatsapp: { sources: split(waSources) }
      } });
      notifySuccess(t('voice.toast.saved'));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('voice.toast.failed'));
    }
  }

  async function confirmReason() {
    if (!reasonFor || !reason.trim()) return;
    try {
      if (reasonFor.kind === 'kill') {
        const res = await kill.mutateAsync(reason.trim());
        notifySuccess(t('voice.toast.killed', { count: res.data.calls_ended }));
      } else if (reasonFor.kind === 'suspend') {
        await updateTenant.mutateAsync({ organisationId: reasonFor.organisationId, status: 'suspended', reason: reason.trim() });
        notifySuccess(t('voice.toast.tenant'));
      } else {
        await save.mutateAsync({ masterEnabled: reasonFor.kind === 'on', reason: reason.trim() });
        notifySuccess(t(reasonFor.kind === 'on' ? 'voice.toast.on' : 'voice.toast.off'));
      }
      setReasonFor(null);
      setReason('');
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('voice.toast.failed'));
    }
  }

  async function setTenant(organisationId: string, status: voice.VoiceTenantStatus) {
    try {
      await updateTenant.mutateAsync({ organisationId, status });
      notifySuccess(t('voice.toast.tenant'));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('voice.toast.failed'));
    }
  }

  const rows = (tenants.data?.data ?? []).map((r) => ({ ...r, id: r.organisation_id })) as Indexed<voice.VoiceTenant & { id: string }>[];
  const busy = save.isPending || kill.isPending || updateTenant.isPending;

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('voice.eyebrow')} title={t('voice.title')} description={t('voice.description')} />

      {settings.isError && (
        <Alert color="error" role="alert">
          {t('voice.loadFailed')}{' '}
          <Button variant="subtle" size="sm" onClick={() => void settings.refetch()}>{t('action.retry')}</Button>
        </Alert>
      )}

      {/* The master switch. */}
      <Card withBorder className={`rr-voice__master${on ? ' is-on' : ''}`} data-testid="voice-master">
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            <Group gap="sm">
              <Title order={3}>{t(on ? 'voice.master.on' : 'voice.master.off')}</Title>
              <Badge variant="light" color={on ? 'green' : 'gray'}>{t(on ? 'voice.master.badgeOn' : 'voice.master.badgeOff')}</Badge>
            </Group>
            <Text role="bodySmall" c={MUTED}>{t(on ? 'voice.master.onHint' : 'voice.master.offHint')}</Text>
            {current && (
              <Text role="caption" c={MUTED}>
                {t('voice.master.changed', { when: formatDateTime(current.updated_at), who: current.updated_by ?? t('common.none') })}
              </Text>
            )}
          </Stack>
          <Group gap="sm">
            {on ? (
              <>
                <Button variant="default" disabled={busy} onClick={() => setReasonFor({ kind: 'off' })}>{t('voice.master.switchOff')}</Button>
                <Button color="red" disabled={busy} onClick={() => setReasonFor({ kind: 'kill' })}>{t('voice.master.kill')}</Button>
              </>
            ) : (
              <Button disabled={busy || !current} onClick={() => setReasonFor({ kind: 'on' })}>{t('voice.master.switchOn')}</Button>
            )}
          </Group>
        </Group>
        {!on && current && !current.settings.terms_version && (
          <Alert color="warning">{t('voice.master.needsTerms')}</Alert>
        )}
      </Card>

      <ReadinessCard />

      {draft && (
        <>
          <Card withBorder>
            <Stack gap="md">
              <Stack gap="xs">
                <Text role="label">{t('voice.switches.title')}</Text>
                <Text role="bodySmall" c={MUTED}>{t('voice.switches.hint')}</Text>
              </Stack>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {SWITCHES.map((key) => (
                  <Switch
                    key={key}
                    label={t(`voice.switch.${key}`)}
                    description={t(`voice.switch.${key}Hint`)}
                    checked={draft.switches[key]}
                    onChange={(v) => set('switches', { [key]: v } as Partial<Settings['switches']>)}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="md">
              <Stack gap="xs">
                <Text role="label">{t('voice.limits.title')}</Text>
                <Text role="bodySmall" c={MUTED}>{t('voice.limits.hint')}</Text>
              </Stack>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {MONEY_LIMITS.map((key) => (
                  <NumberInput
                    key={key}
                    label={t(`voice.limit.${key}`)}
                    description={t(`voice.limit.${key}Hint`)}
                    value={draft.limits[key] / 100}
                    min={0}
                    onChange={(v) => set('limits', { [key]: Math.round((typeof v === 'number' ? v : 0) * 100) } as Partial<Settings['limits']>)}
                  />
                ))}
                {COUNT_LIMITS.map((key) => (
                  <NumberInput
                    key={key}
                    label={t(`voice.limit.${key}`)}
                    description={t(`voice.limit.${key}Hint`)}
                    value={draft.limits[key]}
                    min={0}
                    onChange={(v) => set('limits', { [key]: typeof v === 'number' ? v : 0 } as Partial<Settings['limits']>)}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </Card>

          <Card withBorder data-testid="voice-fraud">
            <Stack gap="md">
              <Stack gap="xs">
                <Text role="label">{t('voice.fraud.title')}</Text>
                <Text role="bodySmall" c={MUTED}>{t('voice.fraud.hint')}</Text>
              </Stack>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {FRAUD_LIMITS.map((key) => (
                  <NumberInput
                    key={key}
                    label={t(`voice.fraud.${key}`)}
                    description={t(`voice.fraud.${key}Hint`)}
                    value={draft.fraud[key]}
                    min={1}
                    onChange={(v) => set('fraud', { [key]: typeof v === 'number' ? v : 1 } as Partial<Settings['fraud']>)}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </Card>

          <Card withBorder data-testid="voice-numbers-policy">
            <Stack gap="md">
              <Stack gap="xs">
                <Text role="label">{t('voice.numbersPolicy.title')}</Text>
                <Text role="bodySmall" c={MUTED}>{t('voice.numbersPolicy.hint')}</Text>
              </Stack>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                {NUMBER_POLICY.map((key) => (
                  <NumberInput
                    key={key}
                    label={t(`voice.numbersPolicy.${key}`)}
                    description={t(`voice.numbersPolicy.${key}Hint`)}
                    value={draft.numbers[key]}
                    min={0}
                    onChange={(v) => set('numbers', { [key]: typeof v === 'number' ? v : 0 } as Partial<Settings['numbers']>)}
                  />
                ))}
              </SimpleGrid>
              <Textarea
                label={t('voice.numbersPolicy.required_documents')}
                description={t('voice.numbersPolicy.required_documentsHint')}
                value={documents}
                minRows={3}
                autosize
                onChange={(e) => setDocuments(e.currentTarget.value)}
              />
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="md">
              <Text role="label">{t('voice.policy.title')}</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <TextInput label={t('voice.policy.terms')} description={t('voice.policy.termsHint')} value={draft.terms_version} maxLength={40}
                  onChange={(e) => set('terms_version', e.currentTarget.value)} />
                <TextInput label={t('voice.policy.termsUrl')} description={t('voice.policy.termsUrlHint')} value={draft.terms_url} placeholder="https://"
                  onChange={(e) => set('terms_url', e.currentTarget.value)} />
                <Select label={t('voice.policy.approval')} description={t(`voice.policy.approvalHint.${draft.approval_mode}`)} value={draft.approval_mode}
                  onChange={(e) => set('approval_mode', e.currentTarget.value as Settings['approval_mode'])}
                  data={[{ value: 'manual', label: t('voice.policy.approvalManual') }, { value: 'auto', label: t('voice.policy.approvalAuto') }]} />
                <Select label={t('voice.policy.onKill')} description={t('voice.policy.onKillHint')} value={draft.on_kill}
                  onChange={(e) => set('on_kill', e.currentTarget.value as Settings['on_kill'])}
                  data={[{ value: 'end_now', label: t('voice.policy.onKillEnd') }, { value: 'let_finish', label: t('voice.policy.onKillFinish') }]} />
                <Select label={t('voice.policy.aiProvider')} description={t('voice.policy.aiProviderHint')} value={draft.ai.provider}
                  onChange={(e) => set('ai', { provider: e.currentTarget.value })}
                  data={[{ value: 'xai', label: 'xAI Grok Voice' /* i18n-ignore: a product name */ }, { value: 'stub', label: t('voice.policy.aiStub') }]} />
                <TextInput label={t('voice.policy.aiVoice')} description={t('voice.policy.aiVoiceHint')} value={draft.ai.voice} onChange={(e) => set('ai', { voice: e.currentTarget.value })} />
                <TextInput label={t('voice.policy.aiModel')} description={t('voice.policy.aiModelHint')} value={draft.ai.model} onChange={(e) => set('ai', { model: e.currentTarget.value })} />
                <NumberInput label={t('voice.policy.retentionMax')} description={t('voice.policy.retentionMaxHint')} value={draft.recording.max_retention_days} min={1}
                  onChange={(v) => set('recording', { max_retention_days: typeof v === 'number' ? v : 1 })} />
                <NumberInput label={t('voice.policy.retentionDefault')} description={t('voice.policy.retentionDefaultHint')} value={draft.recording.default_retention_days} min={1}
                  onChange={(v) => set('recording', { default_retention_days: typeof v === 'number' ? v : 1 })} />
                <TextInput label={t('voice.policy.countries')} description={t('voice.policy.countriesHint')} value={countries} onChange={(e) => setCountries(e.currentTarget.value)} />
                <Textarea label={t('voice.policy.prefixes')} description={t('voice.policy.prefixesHint')} value={prefixes} minRows={2} onChange={(e) => setPrefixes(e.currentTarget.value)} />
                <Textarea label={t('voice.policy.waSources')} description={t('voice.policy.waSourcesHint')} value={waSources} minRows={3}
                  data-testid="wa-sources" onChange={(e) => setWaSources(e.currentTarget.value)} />
              </SimpleGrid>
              <Stack gap="xs">
                <Text role="label">{t('voice.attested.title')}</Text>
                <Text role="bodySmall" c={MUTED}>{t('voice.attested.hint')}</Text>
              </Stack>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <TextInput label={t('voice.attested.attackTests')} description={t('voice.attested.attackTestsHint')} value={draft.attested.attack_tests_on} placeholder="2026-10-02"
                  onChange={(e) => set('attested', { attack_tests_on: e.currentTarget.value })} />
                <NumberInput label={t('voice.attested.loadTested')} description={t('voice.attested.loadTestedHint')} value={draft.attested.load_tested_calls} min={0}
                  onChange={(v) => set('attested', { load_tested_calls: typeof v === 'number' ? v : 0 })} />
                <Switch label={t('voice.attested.certificate')} description={t('voice.attested.certificateHint')} checked={draft.attested.phone_certificate}
                  onChange={(v) => set('attested', { phone_certificate: v })} />
              </SimpleGrid>
              <Group justify="flex-end">
                <Button loading={save.isPending} onClick={saveSettings}>{t('voice.save')}</Button>
              </Group>
            </Stack>
          </Card>
        </>
      )}

      <Card withBorder>
        <Stack gap="md">
          <Stack gap="xs">
            <Text role="label">{t('voice.nodes.title')}</Text>
            <Text role="bodySmall" c={MUTED}>{t('voice.nodes.hint')}</Text>
          </Stack>
          {(nodes.data?.data ?? []).length === 0 && !nodes.isPending && <EmptyState title={t('voice.nodes.empty')} description={t('voice.nodes.emptyHint')} />}
          {(nodes.data?.data ?? []).map((n) => (
            <Group key={n.id} justify="space-between" className="rr-voice__row">
              <Stack gap="xs">
                <Group gap="sm">
                  <Text role="bodySmall" fw={650}>{n.name}</Text>
                  <Badge variant="light" color={n.connected ? 'green' : 'red'}>{t(n.connected ? 'voice.nodes.connected' : 'voice.nodes.disconnected')}</Badge>
                  {n.draining && <Badge variant="light" color="yellow">{t('voice.nodes.draining')}</Badge>}
                </Group>
                <Text role="caption" c={MUTED}>{n.ari_url}{n.version ? ` · Asterisk ${n.version}` : ''}{n.last_seen_at ? ` · ${t('voice.nodes.seen', { when: formatDateTime(n.last_seen_at) })}` : ''}</Text>
              </Stack>
              <Text role="bodySmall">{t('voice.nodes.calls', { live: n.live_calls, capacity: n.capacity })}</Text>
            </Group>
          ))}
        </Stack>
      </Card>

      <CallsCard />

      <Supply />

      <BansCard />

      <Card withBorder>
        <Stack gap="md">
          <Stack gap="xs">
            <Text role="label">{t('voice.tenants.title')}</Text>
            <Text role="bodySmall" c={MUTED}>{t('voice.tenants.hint')}</Text>
          </Stack>
          {tenants.isError && <Alert color="error" role="alert">{t('voice.loadFailed')}</Alert>}
          {!tenants.isPending && rows.length === 0 && <EmptyState title={t('voice.tenants.empty')} description={t('voice.tenants.emptyHint')} />}
          {rows.length > 0 && (
            <DataTable
              records={rows}
              idAccessor="id"
              columns={[
                { accessor: 'organisation_id', title: t('voice.tenants.organisation'), render: (r) => <TenantName id={r.organisation_id} /> },
                {
                  accessor: 'status', title: t('voice.tenants.status'),
                  render: (r) => (
                    <Stack gap="xs">
                      <Badge variant="light" color={r.status === 'active' ? 'green' : r.status === 'suspended' ? 'red' : r.status === 'applied' ? 'yellow' : 'gray'}>{t(`voice.status.${r.status}`)}</Badge>
                      {r.suspended_reason && <Text role="caption" c={MUTED}>{r.suspended_reason}</Text>}
                      {r.outbound_blocked_at && <Text role="caption" c="var(--rr-color-error)">{t('voice.tenants.outboundBlocked')}</Text>}
                      {r.outbound_blocked_at && r.outbound_blocked_reason && <Text role="caption" c={MUTED}>{r.outbound_blocked_reason}</Text>}
                    </Stack>
                  )
                },
                { accessor: 'limits', title: t('voice.tenants.dailyCap'), render: (r) => formatMinor(r.limits.daily_cap_minor, 'ZAR') },
                { accessor: 'updated_at', title: t('voice.tenants.updated'), render: (r) => formatDateTime(r.updated_at) },
                {
                  accessor: 'actions', title: t('common.actions'),
                  render: (r) => (
                    <Group gap="sm" wrap="nowrap">
                      {r.status !== 'active' && <Button size="xs" variant="default" disabled={busy} onClick={() => void setTenant(r.organisation_id, 'active')}>{t('voice.tenants.activate')}</Button>}
                      {r.status === 'active' && <Button size="xs" variant="default" color="red" disabled={busy} onClick={() => setReasonFor({ kind: 'suspend', organisationId: r.organisation_id })}>{t('voice.tenants.suspend')}</Button>}
                      {r.status !== 'off' && <Button size="xs" variant="subtle" disabled={busy} onClick={() => void setTenant(r.organisation_id, 'off')}>{t('voice.tenants.turnOff')}</Button>}
                      {r.outbound_blocked_at && <Button size="xs" variant="default" disabled={busy} onClick={() => void liftBlock(r.organisation_id)}>{t('voice.tenants.liftBlock')}</Button>}
                    </Group>
                  )
                }
              ]}
            />
          )}
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Stack gap="xs">
            <Text role="label">{t('voice.audit.title')}</Text>
            <Text role="bodySmall" c={MUTED}>{t('voice.audit.hint')}</Text>
          </Stack>
          {!audit.isPending && (audit.data?.data ?? []).length === 0 && <EmptyState title={t('voice.audit.empty')} description={t('voice.audit.emptyHint')} />}
          {(audit.data?.data ?? []).map((a) => (
            <Group key={a.id} justify="space-between" align="flex-start" className="rr-voice__row" wrap="nowrap">
              <Stack gap="xs" style={{ minWidth: 0 }}>
                <Group gap="sm">
                  <Badge variant="light" color={a.severity === 'critical' ? 'red' : a.severity === 'warning' ? 'yellow' : 'gray'}>{t(`voice.audit.severity.${a.severity}`)}</Badge>
                  <Text role="bodySmall">{a.summary}</Text>
                </Group>
                {a.organisation_id && <TenantName id={a.organisation_id} subtle />}
              </Stack>
              <Text role="caption" c={MUTED} style={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.created_at)}{a.actor ? ` · ${a.actor}` : ''}</Text>
            </Group>
          ))}
        </Stack>
      </Card>

      <Modal opened={reasonFor !== null} onClose={() => { setReasonFor(null); setReason(''); }} title={reasonFor ? t(`voice.reason.title.${reasonFor.kind}`) : ''}>
        <Stack gap="md">
          <Text role="bodySmall">{reasonFor ? t(`voice.reason.body.${reasonFor.kind}`) : ''}</Text>
          <Textarea label={t('voice.reason.label')} description={t(reasonFor?.kind === 'suspend' ? 'voice.reason.hintSuspend' : 'voice.reason.hint')} value={reason} minRows={2} onChange={(e) => setReason(e.currentTarget.value)} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => { setReasonFor(null); setReason(''); }}>{t('action.cancel')}</Button>
            <Button color={reasonFor?.kind === 'on' ? undefined : 'red'} loading={busy} disabled={!reason.trim()} onClick={() => void confirmReason()}>
              {reasonFor ? t(`voice.reason.confirm.${reasonFor.kind}`) : ''}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

/**
 * Calls across every organisation. "In progress" is the list an operator
 * needs when something looks wrong: who is on a call now, at what price a
 * minute, with one button to end it.
 */
function CallsCard() {
  const t = useT();
  const [show, setShow] = useState<'live' | 'recent'>('live');
  const calls = useVoiceCalls(show === 'live');
  const end = useEndVoiceCall();
  const rows = (calls.data?.data ?? []) as Indexed<voice.VoiceCall>[];

  const endCall = async (id: string) => {
    try { await end.mutateAsync(id); notifySuccess(t('voice.calls.endedNotice')); }
    catch (e) { notifyError(e instanceof Error ? e.message : t('voice.loadFailed')); }
  };

  return (
    <Card withBorder data-testid="voice-calls">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap="xs">
            <Text role="label">{t('voice.calls.title')}</Text>
            <Text role="bodySmall" c={MUTED}>{t('voice.calls.hint')}</Text>
          </Stack>
          <SegmentedControl
            size="xs" value={show} onChange={(v) => setShow(v as 'live' | 'recent')}
            data={[{ value: 'live', label: t('voice.calls.show.live') }, { value: 'recent', label: t('voice.calls.show.recent') }]}
          />
        </Group>
        {calls.isError && <Alert color="error" role="alert">{t('voice.loadFailed')}</Alert>}
        {!calls.isPending && rows.length === 0 && <EmptyState title={t(show === 'live' ? 'voice.calls.emptyLive' : 'voice.calls.empty')} description={t('voice.calls.emptyHint')} />}
        {rows.length > 0 && (
          <DataTable
            records={rows}
            idAccessor="id"
            columns={[
              { accessor: 'organisation_id', title: t('voice.tenants.organisation'), render: (r) => <TenantName id={r.organisation_id} /> },
              { accessor: 'from', title: t('voice.calls.from'), render: (r) => r.from ?? t('voice.calls.unknown') },
              { accessor: 'to', title: t('voice.calls.to'), render: (r) => r.to ?? '' },
              { accessor: 'started_at', title: t('voice.calls.started'), render: (r) => formatDateTime(r.started_at) },
              {
                accessor: 'status', title: t('voice.calls.state'),
                render: (r) => (
                  <Badge variant="light" color={r.status !== 'ended' ? 'green' : r.end_reason === 'failed' || r.end_reason === 'engine_lost' ? 'red' : 'gray'}>
                    {t(r.status === 'ended' ? `voice.calls.ended.${r.end_reason ?? 'completed'}` : `voice.calls.status.${r.status}`)}
                  </Badge>
                )
              },
              { accessor: 'per_minute_minor', title: t('voice.calls.perMinute'), render: (r) => formatMinor(r.per_minute_minor, 'ZAR') },
              {
                accessor: 'quality', title: t('voice.calls.quality'),
                render: (r) => (r.quality ? <Badge variant="light" color={r.quality === 'good' ? 'green' : r.quality === 'fair' ? 'yellow' : 'red'}>{t(`voice.calls.quality.${r.quality}`)}</Badge> : ''),
              },
              {
                accessor: 'charged_minor', title: t('voice.calls.charged'),
                render: (r) => (r.status === 'ended' ? `${formatMinor(r.charged_minor, 'ZAR')} · ${t('voice.calls.seconds', { seconds: r.billed_seconds })}` : '')
              },
              {
                accessor: 'actions', title: t('common.actions'),
                render: (r) => (r.status !== 'ended'
                  ? <Button size="xs" variant="default" color="red" loading={end.isPending && end.variables === r.id} onClick={() => void endCall(r.id)}>{t('voice.calls.end')}</Button>
                  : null)
              }
            ]}
          />
        )}
      </Stack>
    </Card>
  );
}

/**
 * Addresses the call engine has stopped listening to. The engine bans by
 * itself, after repeated failed sign-ins; this is where a person sees it and,
 * for an address that turns out to be a customer's own office, lifts it.
 */
function BansCard() {
  const t = useT();
  const bans = useVoiceBans();
  const lift = useLiftVoiceBan();
  const rows = (bans.data?.data ?? []).filter((b) => !b.lifted_at);
  const lifted = (bans.data?.data ?? []).filter((b) => b.lifted_at).slice(0, 5);

  const liftBan = async (id: string) => {
    try { await lift.mutateAsync(id); notifySuccess(t('voice.bans.liftedNotice')); }
    catch (e) { notifyError(e instanceof Error ? e.message : t('voice.loadFailed')); }
  };

  return (
    <Card withBorder data-testid="voice-bans">
      <Stack gap="md">
        <Stack gap="xs">
          <Text role="label">{t('voice.bans.title')}</Text>
          <Text role="bodySmall" c={MUTED}>{t('voice.bans.hint')}</Text>
        </Stack>
        {bans.isError && <Alert color="error" role="alert">{t('voice.loadFailed')}</Alert>}
        {!bans.isPending && rows.length === 0 && <EmptyState title={t('voice.bans.empty')} description={t('voice.bans.emptyHint')} />}
        {rows.map((b) => (
          <Group key={b.id} justify="space-between" className="rr-voice__row" wrap="nowrap">
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Group gap="sm">
                <Text role="bodySmall" fw={650}>{b.address}</Text>
                <Badge variant="light" color="red">{t('voice.bans.banned')}</Badge>
              </Group>
              <Text role="caption" c={MUTED}>{t('voice.bans.line', { failures: b.failures, when: formatDateTime(b.banned_at) })}</Text>
            </Stack>
            <Button size="xs" variant="default" loading={lift.isPending && lift.variables === b.id} onClick={() => void liftBan(b.id)}>{t('voice.bans.lift')}</Button>
          </Group>
        ))}
        {lifted.map((b) => (
          <Text key={b.id} role="caption" c={MUTED}>{t('voice.bans.liftedLine', { address: b.address, by: b.lifted_by ?? '', when: formatDateTime(b.lifted_at ?? b.banned_at) })}</Text>
        ))}
      </Stack>
    </Card>
  );
}

/**
 * The go-live checklist, worked out by the service rather than remembered by
 * a person. A blocking line is one that could cost money or break a promise;
 * in production the switch is refused while one is unmet. The rest say what
 * will not work yet, and why.
 */
function ReadinessCard() {
  const t = useT();
  const readiness = useVoiceReadiness();
  const r = readiness.data?.data;
  if (!r) return null;
  const open = r.items.filter((i) => !i.ok);
  return (
    <Card withBorder data-testid="voice-readiness">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap="xs">
            <Text role="label">{t('voice.readiness.title')}</Text>
            <Text role="bodySmall" c={MUTED}>{t(r.enforced ? 'voice.readiness.hintEnforced' : 'voice.readiness.hintAdvisory')}</Text>
          </Stack>
          <Badge variant="light" color={r.complete ? 'green' : r.ready ? 'yellow' : 'red'}>
            {t(r.complete ? 'voice.readiness.complete' : r.ready ? 'voice.readiness.ready' : 'voice.readiness.blocked', { count: open.length })}
          </Badge>
        </Group>
        <Stack gap="xs">
          {r.items.map((i) => (
            <Group key={i.code} gap="sm" wrap="nowrap" align="flex-start" className="rr-voice__check" data-ok={i.ok}>
              <Badge variant="light" color={i.ok ? 'green' : i.blocking ? 'red' : 'yellow'}>{t(i.ok ? 'voice.readiness.item.ok' : i.blocking ? 'voice.readiness.item.blocking' : 'voice.readiness.item.missing')}</Badge>
              <Text role="bodySmall">{i.text}</Text>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}
